import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdLockOpen, MdStar, MdStarBorder } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import {
  AppCard,
  AppDatePicker,
  AppInput,
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
  StatusAlertCard,
  StatusBadge,
  PageLoader,
} from '../../../components/ui';
import { indentStatusBadge } from '../../../data/master/assetOwner';
import {
  WORK_AREA_OPTIONS,
  WORKING_HOURS_OPTIONS,
  UOM_SELECT_OPTIONS,
} from '../../../data/master/indents';
import { equipmentTypeOptionFromLookup } from '../../../repositories/lookup.repository';
import {
  createPortalIndent,
  getPortalBootstrap,
  getPortalIndent,
  listPortalIndents,
  releasePortalIndent,
} from '../../../repositories/publicVendor.repository';
import { formatDobDisplay } from '../../../utils/personName';
import { formatTimePeriod } from '../../../utils/timePeriod';
import { VendorPortalRequestModal } from './VendorPortalRequestModal';

const emptyForm = {
  workArea: 'Project',
  equipmentType: '',
  specification: '',
  quantity: '1',
  uom: 'Nos.',
  capacity: '',
  capacityUom: '',
  requiredFrom: '',
  requiredTill: '',
  workingHours: '12 Hrs',
  remarks: '',
};

export default function VendorPortalPage() {
  const { token } = useParams();
  const toast = useToast();
  const [vendor, setVendor] = useState(null);
  const [indents, setIndents] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [indenterFormEnabled, setIndenterFormEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [releasingId, setReleasingId] = useState(null);
  const [releaseRating, setReleaseRating] = useState(null);
  const [releaseRemark, setReleaseRemark] = useState('');
  const requestModal = useDisclosure();
  const releaseConfirm = useDisclosure();
  const releaseCancelRef = useRef();
  const [selectedIndent, setSelectedIndent] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState(null);

  const load = useCallback(async () => {
    if (!token) {
      setVendor(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Portal uses path token only — never staff bearer / system-config / staff lookups.
      const [boot, indentList] = await Promise.all([
        getPortalBootstrap(token),
        listPortalIndents(token),
      ]);
      setVendor(boot.vendor);
      setIndents(Array.isArray(indentList) ? indentList : []);
      setEquipmentTypes(
        (boot.equipmentTypes || []).map(equipmentTypeOptionFromLookup).filter(Boolean),
      );
      setIndenterFormEnabled(boot.indenterFormEnabled !== false);
    } catch (err) {
      setVendor(null);
      toast({
        title: err?.message || 'Invalid portal link',
        status: 'error',
        position: 'top',
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const lastTwo = useMemo(
    () =>
      [...indents]
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
        .slice(0, 2),
    [indents],
  );

  const equipmentOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({ value: e.name, label: e.name, description: e.category, id: e.id })),
    [equipmentTypes],
  );

  const timePeriod = useMemo(
    () => formatTimePeriod(form.requiredFrom, form.requiredTill),
    [form.requiredFrom, form.requiredTill],
  );

  const patch = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(partial).forEach((k) => delete next[k]);
      return next;
    });
  };

  if (loading) {
    return <PageLoader label="Loading portal…" />;
  }

  if (!vendor || vendor.verificationStatus !== 'Verified') {
    const blocked = vendor?.verificationStatus === 'Blocked';
    return (
      <AppCard>
        <StatusAlertCard
          variant="error"
          title={blocked ? 'Vendor blocked' : 'Link not valid'}
          body={
            blocked
              ? vendor.blockReason || 'This vendor cannot raise requests.'
              : 'This request link is missing, expired, or the vendor is not verified. Ask Utkarsh staff to verify the vendor again — a new link is generated then.'
          }
        />
      </AppCard>
    );
  }

  const validate = () => {
    const e = {};
    if (!form.equipmentType) e.equipmentType = 'Select equipment type';
    if (!form.requiredFrom) e.requiredFrom = 'Required from date is required';
    if (!form.requiredTill) e.requiredTill = 'Required till date is required';
    if (
      form.requiredFrom &&
      form.requiredTill &&
      form.requiredTill < form.requiredFrom
    ) {
      e.requiredTill = 'Must be on or after From date';
    }
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        title: 'Please fix highlighted fields',
        status: 'warning',
        position: 'top',
      });
      return;
    }
    const typeRow = equipmentTypes.find((t) => t.name === form.equipmentType);
    if (!typeRow?.id) {
      toast({ title: 'Invalid equipment type', status: 'error', position: 'top' });
      return;
    }

    setSaving(true);
    try {
      await createPortalIndent(token, {
        vendorDbId: vendor.id,
        vendorId: vendor.vendorId,
        buDbId: vendor.buDbId,
        buId: vendor.buId,
        siteDbId: vendor.siteDbId,
        siteId: vendor.siteId,
        workArea: form.workArea,
        raisedByName: vendor.ownerFullName || vendor.tradeAgencyName,
        raisedByEmail: vendor.email || '',
        line: {
          equipmentTypeId: typeRow.id,
          equipmentType: form.equipmentType,
          specification: form.specification.trim() || form.equipmentType,
          quantity: Number(form.quantity) || 1,
          uom: form.uom,
          capacity: form.capacity.trim(),
          capacityUom: form.capacityUom.trim(),
          requiredFrom: form.requiredFrom,
          requiredTill: form.requiredTill,
          timePeriod: formatTimePeriod(form.requiredFrom, form.requiredTill),
          workingHours: form.workingHours,
          remarks: form.remarks.trim(),
        },
      });
      toast({
        title: 'Request submitted',
        status: 'success',
        position: 'top',
      });
      setForm(emptyForm);
      await load();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to create request',
        status: 'error',
        position: 'top',
      });
    } finally {
      setSaving(false);
    }
  };

  const openReleaseConfirm = async (indent) => {
    if (!indent?.id) return;
    setReleaseRating(null);
    setReleaseRemark('');
    setReleaseTarget(indent);
    releaseConfirm.onOpen();
    // List rows have assignmentSummary; fetch detail if reg no is missing.
    if (!indent.assignmentSummary?.regNo && !indent.assignment?.equipment?.regNo) {
      try {
        const detail = await getPortalIndent(token, indent.id);
        setReleaseTarget((prev) => (prev?.id === indent.id ? { ...prev, ...detail } : prev));
      } catch {
        /* keep list row as-is */
      }
    }
  };

  const releaseVehicleLabel = (() => {
    const regNo =
      releaseTarget?.assignmentSummary?.regNo ||
      releaseTarget?.assignment?.equipment?.regNo ||
      '';
    const assetCode =
      releaseTarget?.assignmentSummary?.assetCode ||
      releaseTarget?.assignment?.assetId ||
      releaseTarget?.assignment?.equipment?.assetId ||
      '';
    return [assetCode, regNo].filter(Boolean).join(' · ');
  })();

  const handleRelease = async () => {
    if (!releaseTarget?.id) return;
    setReleasingId(releaseTarget.id);
    try {
      const remark = releaseRemark.trim();
      await releasePortalIndent(token, releaseTarget.id, {
        releaseNote: remark || null,
        releaseRating: releaseRating || null,
      });
      releaseConfirm.onClose();
      setReleaseTarget(null);
      setReleaseRating(null);
      setReleaseRemark('');
      toast({
        title: 'Equipment released',
        status: 'success',
        position: 'top',
      });
      await load();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to release',
        status: 'error',
        position: 'top',
      });
    } finally {
      setReleasingId(null);
    }
  };

  const openRequest = async (indent) => {
    if (!indent?.id) return;
    setSelectedIndent(indent);
    requestModal.onOpen();
    setDetailLoading(true);
    try {
      const detail = await getPortalIndent(token, indent.id);
      setSelectedIndent(detail);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load request',
        status: 'error',
        position: 'top',
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeRequest = () => {
    requestModal.onClose();
    setSelectedIndent(null);
  };

  return (
    <Box maxW="920px" mx="auto">
      <Heading size="lg" mb={1}>
        {vendor.tradeAgencyName}
      </Heading>
      <Text color="text.secondary" mb={6}>
        Vendor request portal · {vendor.vendorId}
      </Text>

      <AppCard mb={6}>
        <Heading size="sm" mb={3}>
          Recent requests
        </Heading>
        {lastTwo.length === 0 ? (
          <Text color="text.secondary">No requests yet.</Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            {lastTwo.map((ind) => (
              <Flex
                key={ind.id || ind.indentId}
                justify="space-between"
                align="center"
                gap={3}
                p={3}
                bg="bg.input"
                borderRadius="sm"
                borderWidth="1px"
                borderColor="border.subtle"
                wrap="wrap"
                cursor="pointer"
                _hover={{ borderColor: 'utkarsh.amber' }}
                onClick={() => openRequest(ind)}
              >
                <Box>
                  <Text fontWeight="600">
                    {ind.line?.equipmentType || ind.line?.specification || '—'}
                  </Text>
                  <Text fontSize="13px" color="text.secondary" fontFamily="mono">
                    {ind.indentId}
                  </Text>
                  <Text fontSize="12px" color="text.secondary">
                    {formatDobDisplay(ind.line?.requiredFrom)} →{' '}
                    {formatDobDisplay(ind.line?.requiredTill)}
                  </Text>
                </Box>
                <Flex align="center" gap={2}>
                  <StatusBadge status={indentStatusBadge(ind.status)}>
                    {ind.status}
                  </StatusBadge>
                  {ind.status === 'Assigned' && !ind.assignment?.releasedAt ? (
                    <OutlineButton
                      size="sm"
                      leftIcon={<MdLockOpen />}
                      isLoading={releasingId === ind.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        openReleaseConfirm(ind);
                      }}
                    >
                      Release
                    </OutlineButton>
                  ) : null}
                </Flex>
              </Flex>
            ))}
          </VStack>
        )}
      </AppCard>

      {indenterFormEnabled ? (
        <AppCard as="form" onSubmit={handleSubmit}>
          <Heading size="sm" mb={4}>
            New equipment request
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
            <AppSelect
              label="Work area"
              value={form.workArea}
              onChange={(v) => patch({ workArea: v })}
              options={WORK_AREA_OPTIONS.map((w) => ({ value: w, label: w }))}
            />
            <AppSelect
              label="Equipment type"
              isRequired
              isSearchable
              value={form.equipmentType}
              onChange={(v) => patch({ equipmentType: v || '' })}
              options={equipmentOptions}
              isInvalid={!!errors.equipmentType}
              errorMessage={errors.equipmentType}
            />
            <AppInput
              label={
                form.equipmentType
                  ? `Specification ${form.equipmentType}`
                  : 'Specification'
              }
              value={form.specification}
              onChange={(e) => patch({ specification: e.target.value })}
              placeholder={
                form.equipmentType
                  ? `What ${form.equipmentType} specification do you need?`
                  : 'Select equipment type, then describe the spec'
              }
              helperText="Describe the spec you need for the selected equipment."
            />
            <AppInput
              label="Quantity"
              value={form.quantity}
              onChange={(e) => patch({ quantity: e.target.value })}
            />
            <AppSelect
              label="UOM"
              value={form.uom}
              onChange={(v) => patch({ uom: v })}
              options={UOM_SELECT_OPTIONS}
            />
            <AppSelect
              label="Working hours"
              value={form.workingHours}
              onChange={(v) => patch({ workingHours: v })}
              options={WORKING_HOURS_OPTIONS.map((h) => ({ value: h, label: h }))}
            />
            <AppDatePicker
              label="Required from"
              isRequired
              value={form.requiredFrom}
              onChange={(e) => patch({ requiredFrom: e.target.value })}
              isInvalid={!!errors.requiredFrom}
              errorMessage={errors.requiredFrom}
            />
            <AppDatePicker
              label="Required till"
              isRequired
              value={form.requiredTill}
              onChange={(e) => patch({ requiredTill: e.target.value })}
              isInvalid={!!errors.requiredTill}
              errorMessage={errors.requiredTill}
            />
          </SimpleGrid>
          {timePeriod ? (
            <Text fontSize="13px" color="text.secondary" mb={3}>
              Period: {timePeriod}
            </Text>
          ) : null}
          <AppTextarea
            label="Remarks"
            value={form.remarks}
            onChange={(e) => patch({ remarks: e.target.value })}
            rows={3}
            mb={4}
          />
          <PrimaryButton type="submit" isLoading={saving}>
            Submit request
          </PrimaryButton>
        </AppCard>
      ) : (
        <StatusAlertCard
          title="Requests temporarily closed"
          body="Indenter form is disabled. Contact Utkarsh staff."
        />
      )}

      <VendorPortalRequestModal
        isOpen={requestModal.isOpen}
        onClose={closeRequest}
        indent={selectedIndent}
        loading={detailLoading}
        portalToken={token}
        onAssigned={async () => {
          await load();
        }}
      />

      <AlertDialog
        isOpen={releaseConfirm.isOpen}
        leastDestructiveRef={releaseCancelRef}
        onClose={() => {
          if (releasingId) return;
          releaseConfirm.onClose();
          setReleaseTarget(null);
          setReleaseRating(null);
          setReleaseRemark('');
        }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Confirm release
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={4}>
                Release{' '}
                <Text as="span" fontWeight="700">
                  {releaseVehicleLabel || 'equipment'}
                </Text>
                {releaseTarget?.indentId ? (
                  <>
                    {' '}
                    for{' '}
                    <Text as="span" fontWeight="700">
                      {releaseTarget.indentId}
                    </Text>
                  </>
                ) : null}
                ? It will become available again.
              </Text>
              <Box mb={3}>
                <Text fontSize="13px" fontWeight="600" mb={1}>
                  Rating (optional)
                </Text>
                <HStack spacing={1}>
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = releaseRating != null && n <= releaseRating;
                    return (
                      <Button
                        key={n}
                        variant="ghost"
                        size="sm"
                        minW="36px"
                        px={1}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                        onClick={() =>
                          setReleaseRating((prev) => (prev === n ? null : n))
                        }
                        isDisabled={Boolean(releasingId)}
                      >
                        <Box
                          as={active ? MdStar : MdStarBorder}
                          boxSize="22px"
                          color={active ? 'brand.500' : 'gray.400'}
                        />
                      </Button>
                    );
                  })}
                </HStack>
                <Text fontSize="12px" color="text.secondary" mt={1}>
                  How did this vehicle work? Tap again to clear.
                </Text>
              </Box>
              <AppTextarea
                label="Remark (optional)"
                value={releaseRemark}
                onChange={(e) => setReleaseRemark(e.target.value)}
                rows={3}
                placeholder="Any note for later review…"
                isDisabled={Boolean(releasingId)}
              />
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <OutlineButton
                ref={releaseCancelRef}
                onClick={() => {
                  releaseConfirm.onClose();
                  setReleaseTarget(null);
                  setReleaseRating(null);
                  setReleaseRemark('');
                }}
                isDisabled={Boolean(releasingId)}
              >
                Cancel
              </OutlineButton>
              <PrimaryButton onClick={handleRelease} isLoading={Boolean(releasingId)}>
                Yes, release
              </PrimaryButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
